/**
 * Navigation component for routing between pages
 * Styled to match Figma header: Healthesus logomark (always), product name (md+ only), nav links (inline from nav-primary-inline up; hamburger below), notifications/messages, user menu.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, MessageSquare, ChevronDown, Menu, X, Pencil, Award, LogOut, ChevronRight } from 'lucide-react';
import { getMyPendingApprovals } from '../../api/clinicStaffProfiles.api';
import { useAuth } from '../../context/AuthContext';
import { useActiveRole } from '../../context/ActiveRoleContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { AppearancePopover } from '../Appearance/AppearancePopover';
import { useApprovalsPushContext } from '../../context/ApprovalsPushContext';
import { useApprovalsPushEligibility } from '../../hooks/useApprovalsPushEligibility';
import { RoleSwitcher } from '../RoleSwitcher/RoleSwitcher';
import { useLogout } from '../../modules/auth';
import { PENDING_APPROVALS_SUMMARY_QUERY_KEY } from '../../constants/approvalsQueryKeys';
import { PORTAL_PRODUCT_NAME_FULL } from '../../constants/portalBrandingConstants';
import { PENDING_APPROVALS_POLL_INTERVAL_MS } from '../../config/navigation.config';
import { useHealthesusLogomarkSrc } from '../../hooks/useHealthesusLogomarkSrc';
import { useLgOrWider } from '../../hooks/useNavPrimaryInlineBreakpoint';
import './Navigation.css';

const NAV_ICON_SIZE = 20;
const NAV_ICON_STROKE = 2;
const NOTIFICATION_READ_KEY = 'notification_read_map_ids';
const NOTIFICATION_DROPDOWN_MAX_ITEMS = 3;
const NOTIFICATION_ANIMATION_DURATION_MS = 1200;
const NOTIFICATION_BANNER_VISIBLE_MS = 5000;
const PUSH_NEW_ITEM_ANNOUNCE = 'New approval request received';

function getReadMapIds(): number[] {
  try {
    const raw = sessionStorage.getItem(NOTIFICATION_READ_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is number => typeof x === 'number') : [];
  } catch {
    return [];
  }
}

function setReadMapIds(ids: number[]): void {
  try {
    sessionStorage.setItem(NOTIFICATION_READ_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

function formatRequestedTimeAgo(isoDate: string): string {
  const d = new Date(isoDate);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return '1d ago';
  return `${diffDays}d ago`;
}

function formatRequestedTimestamp(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const Navigation: React.FC = () => {
  const { isAuthenticated, user, userProfile: contextUserProfile, isLoggingIn, isLoading } = useAuth();
  const { logout } = useLogout();
  const healthesusLogomarkSrc = useHealthesusLogomarkSrc();
  /** Tablet/desktop (`lg`, 1024px+): keep Appearance in header; narrower viewports use the slide-down menu only. */
  const isLgOrWider = useLgOrWider();
  const { activeRole, availableRoles } = useActiveRole();
  // Use stored userProfile from context first, fallback to fetching if not available
  const { data: fetchedUserProfile } = useUserProfile(contextUserProfile ? null : (user?.id || null));
  const userProfile = contextUserProfile || fetchedUserProfile;
  
  // Defensive check: Hide user info if login_error exists (prevents showing stale auth state)
  const hasLoginError = sessionStorage.getItem('login_error') !== null;
  // Only show menu items (Dashboard, Forms, user menu) when sign-in is fully complete (MSAL + UserService both succeeded)
  const isFullySignedIn = isAuthenticated && !!user && !hasLoginError;
  // Only show logged-in user section when full profile (display name) is available; keep it invisible until then
  const hasProfileDisplayName = !!(userProfile?.firstName || userProfile?.lastName || userProfile?.userName);
  const shouldShowUser = isFullySignedIn && !isLoggingIn && !isLoading && hasProfileDisplayName;
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(() => !document.hidden);
  const [notificationEffect, setNotificationEffect] = useState(false);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [bellHighlighted, setBellHighlighted] = useState(false);
  const [notificationBadgeBounce, setNotificationBadgeBounce] = useState(false);
  const [newItemIds, setNewItemIds] = useState<number[]>([]);
  const [liveAnnounceText, setLiveAnnounceText] = useState('');
  const notificationsRef = useRef<HTMLDivElement>(null);
  const notificationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousPendingCountRef = useRef<number>(0);
  const previousMapIdsRef = useRef<Set<number>>(new Set());
  const seenInDropdownRef = useRef<Set<number>>(new Set());
  const latestPendingMapIdsRef = useRef<Set<number>>(new Set());
  const pushJustReceivedRef = useRef(false);
  const badgeBounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleVisibilityChange = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
      if (badgeBounceTimeoutRef.current) clearTimeout(badgeBounceTimeoutRef.current);
    };
  }, []);

  // Removed isMinimized state - navigation stays static
  const isLoggingOutRef = React.useRef(false);
  const [readMapIds, setReadMapIdsState] = useState<number[]>(() => getReadMapIds());

  // Check if user is in registration process
  // Note: complete-license page is NOT considered a registration page - user has completed registration
  const isRegistrationPage = 
    location.pathname === '/account-setup' ||
    location.pathname === '/choose-role' ||
    location.pathname.startsWith('/registration');
  
  // Check if we're on the complete-license page
  const isCompleteLicensePage = location.pathname === '/complete-license';

  const handleLogout = async () => {
    // Prevent multiple simultaneous logout attempts
    if (isLoggingOutRef.current) {
      return;
    }
    
    // Prevent navigation if already on sign in page
    if (location.pathname === '/login') {
      return;
    }
    
    isLoggingOutRef.current = true;
    
    try {
      await logout();
      
      // Don't manually navigate - let ProtectedRoute handle the redirect
      // This prevents multiple navigation attempts and the throttling warning
      // ProtectedRoute will automatically redirect to /login when isAuthenticated becomes false
      // The isLoggingOut flag ensures ProtectedRoute waits before redirecting
      
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, let ProtectedRoute handle redirect (fail-open semantics)
    } finally {
      // Reset logout flag after a delay to allow ProtectedRoute to handle redirect
      // This gives time for the auth state to update and ProtectedRoute to react
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 300);
    }
  };

  const isActive = (path: string) => {
    if (path === '/form') {
      return location.pathname.startsWith('/form');
    }
    if (path === '/provider-approvals') {
      return location.pathname === path || location.pathname.startsWith(path + '/');
    }
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserMenu && !target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserMenu]);

  // Removed scroll-based collapse behavior - navigation stays static

  // Convert string to Pascal case (capitalize first letter of each word)
  const toPascalCase = (str: string): string => {
    if (!str) return str;
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getRoleName = (role: unknown): string => {
    if (typeof role === 'string') return role;
    const r = role as { roleName?: string; name?: string };
    return (r?.roleName ?? r?.name ?? '').toLowerCase();
  };

  // Check if user has Provider role
  const hasProviderRole = () => {
    const profileRoles = userProfile?.roles || [];
    const activeRoleString = activeRole != null ? String(activeRole) : null;
    return (
      (activeRoleString && activeRoleString.toLowerCase() === 'provider') ||
      availableRoles.some(role => typeof role === 'string' && role.toLowerCase() === 'provider') ||
      user?.roles?.some(role => typeof role === 'string' && role.toLowerCase() === 'provider') ||
      (Array.isArray(profileRoles) && profileRoles.some((role: unknown) => getRoleName(role) === 'provider'))
    );
  };

  // Check if user has Provider or Office Administrator role and User.View permission (for Approval Requests nav link)
  const { showNotifications, numericUserId } = useApprovalsPushEligibility();

  const notifyAnimationCompleteRef = useRef<() => void>(() => {});

  const onPushReceived = useCallback(() => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
    previousMapIdsRef.current = new Set(latestPendingMapIdsRef.current);
    setNotificationEffect(true);
    setShowNotificationBanner(true);
    setBellHighlighted(true);
    setLiveAnnounceText(PUSH_NEW_ITEM_ANNOUNCE);
    pushJustReceivedRef.current = true;

    notificationTimeoutRef.current = setTimeout(() => {
      setNotificationEffect(false);
      notificationTimeoutRef.current = null;
    }, NOTIFICATION_ANIMATION_DURATION_MS);

    setTimeout(() => {
      setShowNotificationBanner(false);
      setLiveAnnounceText('');
      notifyAnimationCompleteRef.current();
    }, NOTIFICATION_BANNER_VISIBLE_MS);
  }, []);

  const { isConnected: approvalsPushConnected, notifyAnimationComplete, registerPushAnimationListener } =
    useApprovalsPushContext();
  notifyAnimationCompleteRef.current = notifyAnimationComplete;

  useEffect(() => {
    return registerPushAnimationListener(onPushReceived);
  }, [registerPushAnimationListener, onPushReceived]);

  const { data: pendingApprovalsResponse, refetch: refetchPendingApprovals } = useQuery({
    queryKey: [PENDING_APPROVALS_SUMMARY_QUERY_KEY, numericUserId],
    queryFn: async () => {
      try {
        return await getMyPendingApprovals({ userId: numericUserId! });
      } catch (err) {
        console.error('[Navigation] Pending approvals fetch failed:', err);
        throw err;
      }
    },
    enabled: showNotifications && !!numericUserId,
    refetchInterval: showNotifications && isTabVisible && !approvalsPushConnected ? PENDING_APPROVALS_POLL_INTERVAL_MS : 0,
    refetchIntervalInBackground: false,
    retry: true,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 0,
  });

  const allPendingItems = pendingApprovalsResponse?.data?.items ?? [];
  const pendingCount = pendingApprovalsResponse?.data?.count ?? allPendingItems.length;
  const pendingItems = React.useMemo(() => {
    const sorted = [...allPendingItems].sort(
      (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );
    return sorted.slice(0, NOTIFICATION_DROPDOWN_MAX_ITEMS);
  }, [allPendingItems]);

  useEffect(() => {
    latestPendingMapIdsRef.current = new Set(allPendingItems.map((i) => i.mapId));
  }, [allPendingItems]);

  useEffect(() => {
    if (!pushJustReceivedRef.current) {
      previousPendingCountRef.current = pendingCount;
      return;
    }
    // Only run badge/new-items logic when refetch has updated data (avoid race where effect ran with stale data)
    const prevCount = previousPendingCountRef.current;
    const newItemIdsFromData = allPendingItems
      .filter((i) => !previousMapIdsRef.current.has(i.mapId))
      .map((i) => i.mapId);
    const countChanged = pendingCount !== prevCount;
    const hasNewItems = newItemIdsFromData.length > 0;
    if (!countChanged && !hasNewItems) {
      return; // Wait for refetch to complete; effect will run again with new data
    }
    pushJustReceivedRef.current = false;
    setNewItemIds((prev) => [...new Set([...prev, ...newItemIdsFromData])]);
    previousPendingCountRef.current = pendingCount;
    if (badgeBounceTimeoutRef.current) clearTimeout(badgeBounceTimeoutRef.current);
    setNotificationBadgeBounce(true);
    badgeBounceTimeoutRef.current = setTimeout(() => {
      setNotificationBadgeBounce(false);
      badgeBounceTimeoutRef.current = null;
    }, 500);
  }, [pendingCount, allPendingItems]);

  const markNotificationAsRead = (mapId: number) => {
    const next = readMapIds.includes(mapId) ? readMapIds : [...readMapIds, mapId];
    setReadMapIdsState(next);
    setReadMapIds(next);
    setNewItemIds((prev) => prev.filter((id) => id !== mapId));
  };

  useEffect(() => {
    if (showNotificationsDropdown) setReadMapIdsState(getReadMapIds());
  }, [showNotificationsDropdown]);

  useEffect(() => {
    if (!showNotificationsDropdown && pendingItems.length > 0) {
      seenInDropdownRef.current = new Set(pendingItems.map((i) => i.mapId));
      setNewItemIds([]);
    }
  }, [showNotificationsDropdown, pendingItems]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setShowNotificationsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get display name: FirstName + LastName with "Dr." prefix if Provider role.
  // Only show name when userProfile is available; never show email in header.
  const getDisplayName = () => {
    const firstName = userProfile?.firstName;
    const lastName = userProfile?.lastName;
    const isProvider = hasProviderRole();
    
    if (firstName && lastName) {
      const pascalFirstName = toPascalCase(firstName);
      const pascalLastName = toPascalCase(lastName);
      const fullName = `${pascalFirstName} ${pascalLastName}`.trim();
      return isProvider ? `Dr. ${fullName}` : fullName;
    }
    
    if (firstName) {
      const pascalFirstName = toPascalCase(firstName);
      return isProvider ? `Dr. ${pascalFirstName}` : pascalFirstName;
    }
    
    if (lastName) {
      const pascalLastName = toPascalCase(lastName);
      return isProvider ? `Dr. ${pascalLastName}` : pascalLastName;
    }
    
    if (userProfile?.userName) {
      return toPascalCase(userProfile.userName);
    }
    
    // Do not show email; use neutral label until profile (full name) is available
    return 'Loading...';
  };

  const getRoleLabel = (): string => {
    const role = activeRole != null ? String(activeRole).trim() : '';
    if (!role) return 'User';
    return toPascalCase(role);
  };

  // Get user initials for avatar - uses firstName and lastName from userProfile only.
  // Do not use email for initials; use "U" until profile is available.
  const getUserInitials = () => {
    const firstName = userProfile?.firstName;
    const lastName = userProfile?.lastName;
    
    if (firstName && lastName) {
      const firstInitial = firstName.charAt(0).toUpperCase();
      const lastInitial = lastName.charAt(0).toUpperCase();
      return `${firstInitial}${lastInitial}`;
    }
    
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    
    if (lastName) {
      return lastName.substring(0, 2).toUpperCase();
    }
    
    if (userProfile?.userName) {
      return userProfile.userName.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  };

  // Navigation stays static - no minimize behavior
  const navClassName = `bg-white dark:bg-[#18191a] border-b border-gray-200 dark:border-[#3a3b3c] sticky top-0 z-[10000] shadow-sm`;

  // Set static navigation height
  useEffect(() => {
    const navHeight = 64; // Static height - no dynamic changes
    document.documentElement.style.setProperty('--nav-height', `${navHeight}px`);
    document.documentElement.style.setProperty('--header-height', `${navHeight}px`);
  }, []);

  const navHeight = 64; // Static height

  const headerAppearanceControl =
    isCompleteLicensePage || isLgOrWider ? (
      <div className="flex items-center">
        <AppearancePopover />
      </div>
    ) : null;

  return (
    <nav 
      className={navClassName}
      role="navigation" 
      aria-label="Main navigation"
      style={{ '--nav-height': `${navHeight}px` } as React.CSSProperties}
    >
      <div className="nav-header-inner mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <div className="nav-header-row flex justify-between items-center min-h-[var(--nav-height)]">
          {/* Left: Figma layout – logo + product name + nav links in one row */}
          <div className="nav-header-left flex items-center min-w-0 flex-1">
            <Link
              to="/"
              className="nav-header-brand flex items-center flex-shrink-0 no-underline text-inherit hover:opacity-90 transition-opacity"
              aria-label={`Healthesus — ${PORTAL_PRODUCT_NAME_FULL} home`}
              title={PORTAL_PRODUCT_NAME_FULL}
            >
              <div className="nav-header-logomark-wrap shrink-0">
                <img
                  src={healthesusLogomarkSrc}
                  alt=""
                  className="nav-header-logo shrink-0"
                  width={32}
                  height={32}
                />
              </div>
              <span className="nav-header-product-name hidden md:inline whitespace-nowrap">
                {PORTAL_PRODUCT_NAME_FULL}
              </span>
            </Link>
            {/* Main Navigation – Figma: links immediately after product name */}
            {isFullySignedIn && !isRegistrationPage && !isCompleteLicensePage && (
              <nav className="nav-header-links hidden nav-primary-inline:flex items-center flex-shrink-0" aria-label="Primary">
                <Link
                  to="/dashboard"
                  className={`nav-header-link px-3 py-2 rounded-lg no-underline ${
                    isActive('/dashboard') ? 'nav-header-link-active' : ''
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/form/DBQ_SCARS?formCode=DBQ_SCARS&formVersion=v1.0"
                  className={`nav-header-link px-3 py-2 rounded-lg no-underline ${
                    isActive('/form') ? 'nav-header-link-active' : ''
                  }`}
                >
                  Forms
                </Link>
                {showNotifications && (
                  <Link
                    to="/provider-approvals"
                    onClick={() => { if (numericUserId) void refetchPendingApprovals(); }}
                    className={`nav-header-link px-3 py-2 rounded-lg no-underline ${
                      isActive('/provider-approvals') ? 'nav-header-link-active' : ''
                    }`}
                  >
                    Approval Requests
                  </Link>
                )}
              </nav>
            )}
          </div>

          {/* Right: Figma layout – icon buttons, appearance, then user block */}
          <div className="nav-header-right flex items-center flex-shrink-0">
            {shouldShowUser ? (
              <>
                {isRegistrationPage ? headerAppearanceControl : null}

                {/* Notifications - for Provider / Office Admin with User.View only */}
                {!isRegistrationPage && showNotifications && (
                  <div ref={notificationsRef} className="relative">
                    <div
                      className={`notification-bell relative ${notificationEffect ? 'new-notification' : ''} ${bellHighlighted && !showNotificationsDropdown ? 'notification-bell-highlighted' : ''}`}
                    >
                      {liveAnnounceText ? (
                        <span className="sr-only" aria-live="polite" aria-atomic="true">
                          {liveAnnounceText}
                        </span>
                      ) : null}
                      {notificationEffect ? (
                        <>
                          <div className="notification-bell-glow" aria-hidden />
                          <div className="notification-bell-glow notification-bell-glow-2" aria-hidden />
                        </>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          const next = !showNotificationsDropdown;
                          setShowNotificationsDropdown(next);
                          if (next) {
                            setBellHighlighted(false);
                            if (numericUserId) void refetchPendingApprovals();
                          }
                        }}
                        className="nav-header-icon-btn relative p-2 text-gray-600 dark:text-[#b0b3b8] hover:text-gray-900 dark:hover:text-[#e4e6eb] hover:bg-gray-100 dark:hover:bg-[#2f3031] rounded-lg transition-all duration-300"
                        aria-label="Notifications"
                        aria-expanded={showNotificationsDropdown}
                      >
                        <Bell className="nav-header-icon shrink-0" size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} aria-hidden />
                        {pendingCount > 0 && (
                          <span
                            className={`absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center min-w-[1.25rem] h-5 ${
                              notificationBadgeBounce ? 'notification-badge-bounce' : ''
                            }`}
                          >
                            {pendingCount > 99 ? '99+' : pendingCount}
                          </span>
                        )}
                      </button>
                      {showNotificationBanner && (
                        <button
                          type="button"
                          className="notification-banner"
                          onClick={() => {
                            setShowNotificationsDropdown(true);
                            setBellHighlighted(false);
                            setShowNotificationBanner(false);
                            if (numericUserId) void refetchPendingApprovals();
                          }}
                          role="status"
                          aria-live="polite"
                        >
                          New approval request received
                        </button>
                      )}
                    </div>
                    {showNotificationsDropdown && (
                      <div
                        className="flex flex-col overflow-hidden rounded-lg border bg-white dark:bg-[#242526] border-gray-200 dark:border-[#3a3b3c] shadow-lg
                          max-md:fixed max-md:left-4 max-md:right-4 max-md:top-[calc(var(--nav-height,64px)+0.5rem)] max-md:z-[10060] max-md:w-auto max-md:max-h-[min(24rem,calc(100dvh-var(--nav-height,64px)-1.5rem))]
                          md:absolute md:inset-x-auto md:left-auto md:right-0 md:top-full md:mt-2 md:z-50 md:w-80 md:max-h-96"
                        role="dialog"
                        aria-label="Notifications"
                      >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-[#3a3b3c]">
                          <span className="font-semibold text-gray-900 dark:text-[#e4e6eb]">Notifications</span>
                          <span className="text-sm text-gray-600 dark:text-[#b0b3b8]">{pendingCount} Pending</span>
                        </div>
                        <div className="overflow-y-auto flex-1">
                          {pendingItems.length === 0 ? (
                            <div className="px-4 py-4 text-sm text-gray-500 dark:text-[#b0b3b8]">
                              No pending approval requests
                            </div>
                          ) : (
                            pendingItems.map((item) => {
                              const isUnread = !readMapIds.includes(item.mapId);
                              const title = item.applicant?.roleName
                                ? `${item.applicant.roleName} approval request`
                                : 'Approval request';
                              return (
                                <button
                                  key={item.mapId}
                                  type="button"
                                  onClick={() => {
                                    markNotificationAsRead(item.mapId);
                                    setShowNotificationsDropdown(false);
                                    navigate(`/provider-approvals?highlight=${item.mapId}`);
                                  }}
                                  className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-[#2f3031] border-b border-gray-100 dark:border-[#3a3b3c] last:border-b-0 focus:outline-none focus:bg-gray-50 dark:focus:bg-[#2f3031] ${
                                    newItemIds.includes(item.mapId) ? 'notification-dropdown-item-new' : ''
                                  }`}
                                >
                                  {isUnread && (
                                    <span
                                      className="shrink-0 w-2 h-2 rounded-full mt-1.5 bg-amber-500"
                                      aria-hidden
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 dark:text-[#e4e6eb] text-sm">{title}</div>
                                    <div className="text-sm text-gray-600 dark:text-[#b0b3b8] mt-0.5" title={formatRequestedTimestamp(item.createdDate)}>
                                      {item.applicant?.displayName ?? '—'} · Requested {formatRequestedTimeAgo(item.createdDate)}
                                    </div>
                                    {item.provider?.npiNumber && (
                                      <div className="text-xs text-gray-500 dark:text-[#8a8d91] mt-0.5">NPI: {item.provider.npiNumber}</div>
                                    )}
                                  </div>
                                  <ChevronRight className="shrink-0 w-4 h-4 text-gray-400 mt-1" aria-hidden />
                                </button>
                              );
                            })
                          )}
                        </div>
                        {pendingCount > NOTIFICATION_DROPDOWN_MAX_ITEMS && (
                          <div className="border-t border-gray-200 dark:border-[#3a3b3c] px-4 py-2">
                            <Link
                              to="/provider-approvals"
                              onClick={() => setShowNotificationsDropdown(false)}
                              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline block py-2"
                            >
                              View all approval requests
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Messages - match Figma header right actions order */}
                {!isRegistrationPage && (
                  <button
                    type="button"
                    className="nav-header-icon-btn p-2 text-gray-600 dark:text-[#b0b3b8] hover:text-gray-900 dark:hover:text-[#e4e6eb] hover:bg-gray-100 dark:hover:bg-[#2f3031] rounded-full transition-all duration-300"
                    aria-label="Messages"
                  >
                    <MessageSquare className="nav-header-icon shrink-0" size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} aria-hidden />
                  </button>
                )}

                {/* Appearance: immediately left of user menu; header from lg up or complete-license. */}
                {!isRegistrationPage && headerAppearanceControl}

                {/* User Profile – Figma: light gray container, avatar, name, Online, chevron */}
                {!isRegistrationPage && (
                    <div className="nav-header-user-block user-menu-container relative">
                      <button 
                        className="nav-header-user-block-btn"
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        aria-label="User menu"
                        aria-expanded={showUserMenu}
                        aria-haspopup="menu"
                        type="button"
                      >
                        <div className="nav-header-user-block-btn-inner">
                          <div className="nav-header-user-avatar">
                            {getUserInitials()}
                          </div>
                          <div className="hidden lg:flex lg:items-center lg:min-w-0 lg:flex-1 lg:gap-2">
                            <div className="flex flex-col min-w-0">
                              <span className="nav-header-user-name user-menu-username truncate">
                                {getDisplayName()}
                              </span>
                              <span className="nav-header-status-label flex items-center mt-0.5">
                                <span className="w-2 h-2 rounded-full mr-1.5 bg-green-500 shrink-0" aria-hidden />
                                {getRoleLabel()} • Online
                              </span>
                            </div>
                            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                              <RoleSwitcher />
                            </div>
                          </div>
                          <ChevronDown className="nav-header-icon shrink-0" size={16} strokeWidth={2} aria-hidden />
                        </div>
                      </button>
                      
                      {/* User Dropdown Menu */}
                      {showUserMenu && (
                        <div className="user-menu-dropdown absolute right-0 top-full mt-2 bg-white dark:bg-[#242526] rounded-md shadow-lg border border-gray-200 dark:border-[#3a3b3c] py-1 z-50">
                          <Link
                            to="/profile"
                            state={{ from: location.pathname }}
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-2 px-4 py-2 text-sm transition no-underline"
                          >
                            <Pencil size={16} strokeWidth={2} className="shrink-0" aria-hidden />
                            Edit Profile
                          </Link>
                          {(() => {
                            // Check if user has provider role - multiple fallback checks
                            // 1. Check active role
                            // 2. Check available roles from ActiveRoleContext
                            // 3. Check roles from JWT token (AuthContext)
                            // 4. Check roles from user profile API response (database)
                            const profileRoles = userProfile?.roles || [];
                            const activeRoleString = activeRole != null ? String(activeRole) : null;
                            const hasProviderRole = 
                              (activeRoleString && activeRoleString.toLowerCase() === 'provider') || 
                              availableRoles.some(role => typeof role === 'string' && role.toLowerCase() === 'provider') ||
                              user?.roles?.some(role => typeof role === 'string' && role.toLowerCase() === 'provider') ||
                              (Array.isArray(profileRoles) && profileRoles.some((role: unknown) => getRoleName(role) === 'provider'));
                            
                            return hasProviderRole;
                          })() && (
                            <Link
                              to="/profile/edit-credentials"
                              state={{ from: location.pathname }}
                              onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-2 px-4 py-2 text-sm transition no-underline"
                            >
                              <Award size={16} strokeWidth={2} className="shrink-0" aria-hidden />
                              Edit License & Credentials
                            </Link>
                          )}
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm transition"
                          >
                            <LogOut size={16} strokeWidth={2} className="shrink-0" aria-hidden />
                            Sign out
                          </button>
                        </div>
                      )}
                    </div>
                )}
              </>
            ) : (
              // Unauthenticated nav (non-Home routes only): appearance control — no Sign In. See docs/NAV_UNAUTHENTICATED_SIGN_IN.md
              headerAppearanceControl
            )}

            {/* Mobile Menu Button */}
            {!isCompleteLicensePage && (
              <button
                type="button"
                className="nav-header-icon-btn nav-primary-inline:hidden p-2 text-gray-600 dark:text-[#b0b3b8] hover:text-gray-900 dark:hover:text-[#e4e6eb] hover:bg-gray-100 dark:hover:bg-[#2f3031] rounded-md transition-all duration-300"
                onClick={toggleMobileMenu}
                aria-label="Toggle navigation menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="nav-header-icon shrink-0" size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} aria-hidden />
                ) : (
                  <Menu className="nav-header-icon shrink-0" size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} aria-hidden />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="nav-primary-inline:hidden border-t border-gray-200 dark:border-[#3a3b3c] py-4">
            <div className="flex flex-col space-y-1">
              {!isCompleteLicensePage && !isLgOrWider && (
                <div className="flex items-center px-3 py-2 border-b border-gray-200 dark:border-[#3a3b3c] mb-1">
                  <AppearancePopover />
                </div>
              )}
              {isFullySignedIn && !isRegistrationPage && !isCompleteLicensePage ? (
                <>
                  <Link 
                    to="/dashboard" 
                    onClick={closeMobileMenu}
className={`nav-header-link px-3 py-2 rounded-lg no-underline ${
                      isActive('/dashboard') ? 'nav-header-link-active' : ''
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/form/1" 
                    onClick={closeMobileMenu}
                    className={`nav-header-link px-3 py-2 rounded-lg no-underline ${
                      isActive('/form') ? 'nav-header-link-active' : ''
                    }`}
                  >
                    Forms
                  </Link>
                  {showNotifications && (
                    <Link 
                      to="/provider-approvals" 
                      onClick={() => {
                        if (numericUserId) void refetchPendingApprovals();
                        closeMobileMenu();
                      }}
                      className={`nav-header-link px-3 py-2 rounded-lg no-underline ${
                        isActive('/provider-approvals') ? 'nav-header-link-active' : ''
                      }`}
                    >
                      Approval Requests
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="nav-header-link px-3 py-2 text-left rounded-lg no-underline"
                  >
                    Sign out
                  </button>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

